import { tr } from '@/lib/i18n-core';
import { formatMoneyInput, minorFromInput, moneyDigits } from '@/components/MoneyInput';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Barcode, Trash2 } from 'lucide-react';
import { ApiError, get, post, put, qs } from '@/lib/api';
import type { PosPackageAllowance, PosQuote, SupervisorCredentials } from '@his/shared';
import { SupervisorDialog, needsDiscountApproval, supervisorHeaders } from '../accounts/SupervisorApproval';
import { describeError, useAuth } from '@/lib/auth';
import { PATIENT_BEARING_MODULES } from '@his/shared';
import { dateOnly, humanize, money, newIdempotencyKey } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { visibleInterval } from '@/lib/polling';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable, Dialog, PageHeader, Tabs } from '@/components/ui/layout';
import {
  Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, Select,
} from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { WasfatyDialog } from '../integrations/WasfatyDialog';
import { withReturnTo } from '../insurance/insurance-routes';
import { SalesReturnPanel } from './SalesReturnPanel';
import { PrintDocumentButton } from './PrintDocumentButton';
import { cartAmounts, cartLineAmounts, type PricedBatch } from './cart-pricing';
import { useDebounced } from '../diagnostics/LabBenchDebounce';
import {
  QUOTE_DEBOUNCE_MS,
  QUOTE_FRESH_MS,
  amountToCollect,
  cartPackageView,
  quotableLines,
  quoteSignature,
  refusedDue,
  saleLines,
  type CartLinePackage,
  type PackageBadge,
} from './pos-quote';

const PACKAGE_ALLOWANCE_KEY = 'pos-package-allowance';
const POS_QUOTE_KEY = 'pos-quote';

interface Store {
  id: string;
  code: string;
  name: string;
  type: string;
  isSellingPoint: boolean;
  branch: { id: string; name: string } | null;
}

/**
 * Store names repeat across sites — every branch has a "Main Pharmacy" — so the
 * branch has to be on the label or the picker is a coin toss in a group.
 */
function storeLabel(store: { name: string; branch?: { name: string } | null }): string {
  return store.branch ? `${store.branch.name} — ${store.name}` : store.name;
}

interface SellableProduct {
  id: string;
  code: string;
  genericName: string;
  brandName: string | null;
  form: string | null;
  strength: string | null;
  unit: string;
  isRxRequired: boolean;
  isControlled: boolean;
  available: number;
  priceMinor: number;
  saleTaxPct: number;
  nextExpiry: string | null;
  batches: { id: string; qtyOnHand: number; priceMinor: number }[];
}

interface QueuedRx {
  id: string;
  status: string;
  issuedAt: string;
  patient: { id: string; mrn: string; fullName: string; phone: string | null };
  doctor: { id: string; name: string };
  items: {
    id: string; productId: string | null; drugName: string; strength: string | null;
    dose: string; frequency: string; quantity: number; dispensedQty: number; pendingQty: number;
    isControlled: boolean;
    mappedProductId?: string | null;
    mappedProductName?: string | null;
  }[];
}

function MapProductDialog({
  item,
  storeId,
  onClose,
  onMapped,
}: {
  item: { id: string; drugName: string; strength: string | null };
  storeId: string;
  onClose: () => void;
  onMapped: () => void;
}) {
  const toast = useToast();
  const [term, setTerm] = useState(item.drugName);
  const results = useQuery({
    queryKey: ['pharmacy-search', storeId, term],
    queryFn: () => get<SellableProduct[]>(`/pharmacy/search${qs({ storeId, q: term, limit: 15 })}`),
    enabled: term.trim().length >= 2 && Boolean(storeId),
  });
  const map = useMutation({
    mutationFn: (productId: string) => put(`/pharmacy/prescription-items/${item.id}/map`, { productId }),
    onSuccess: () => {
      toast.success(tr('pharmacy', 'product_chosen'), tr('pharmacy', 'it_is_dispensed_against_this_prescription_line'));
      onMapped();
      onClose();
    },
    onError: (error) => toast.error(tr('pharmacy', 'could_not_use_that_product'), describeError(error)),
  });
  return (
    <Dialog open onClose={onClose} title={`Choose the product for ${item.drugName}${item.strength ? ` ${item.strength}` : ''}`}>
      <div className="space-y-3">
        <Input value={term} onChange={(e) => setTerm(e.target.value)} autoFocus />
        <ul className="max-h-72 divide-y overflow-y-auto rounded-md border text-sm">
          {results.data
            ?.filter((product) => !product.isControlled)
            .map((product) => (
              <li key={product.id}>
                <button type="button" className="w-full px-3 py-2 text-start hover:bg-accent focus-ring" onClick={() => map.mutate(product.id)}>
                  {product.brandName ?? product.genericName} {product.strength ?? ''} {product.form ? `(${product.form})` : ''}
                  <span className="ms-2 text-xs text-muted-foreground">{product.available} {tr('pharmacy', 'in_stock')}</span>
                </button>
              </li>
            ))}
        </ul>
        <p className="text-xs text-muted-foreground">{tr('pharmacy', 'controlled_medicines_are_not_listed_the_doctor')}</p>
      </div>
    </Dialog>
  );
}

interface CartLine {
  productId: string;
  label: string;
  qty: number;
  priceMinor: number;
  available: number;
  discountPct: number;
  taxPct: number;
  batches: PricedBatch[];
  priced: boolean;
  prescriptionItemId?: string;
}

function badgeLabel(badge: PackageBadge): string {
  if (badge.kind === 'in') return badge.qty === null ? tr('pharmacy', 'in_package') : tr('pharmacy', 'x_in_package', { qty: badge.qty });
  return badge.qty === null ? tr('pharmacy', 'beyond_package') : tr('pharmacy', 'x_beyond_package', { qty: badge.qty });
}

function PackageBadges({ view }: { view: CartLinePackage | null }) {
  if (!view || view.badges.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {view.badges.map((badge) => (
        <Badge key={badge.kind} variant={badge.kind === 'in' ? 'success' : 'warning'}>
          {badgeLabel(badge)}
        </Badge>
      ))}
    </div>
  );
}

function LineAmount({ view, fallback }: { view: CartLinePackage | null; fallback: string }) {
  if (!view || view.absorbedMinor <= 0) return <>{fallback}</>;
  return (
    <>
      <s className="block text-xs text-muted-foreground">{money(view.listNetMinor)}</s>
      {money(view.billedNetMinor)}
    </>
  );
}

/**
 * Pharmacy counter (SRS 7.2).
 *
 * Keyboard-first, as the SRS requires for high-volume desks: the search box
 * holds focus, a scanned barcode adds straight to the cart, and F2 completes the
 * sale. Nothing on this screen needs a mouse.
 */
export function PharmacyPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasModule, can } = useAuth();
  const [tab, setTab] = useState('counter');
  const [storeId, setStoreId] = useState('');
  const [wasfatyOpen, setWasfatyOpen] = useState(false);
  const askInsurer = (target: { patientId: string; prescriptionId: string } | 'walk-in') =>
    navigate(
      withReturnTo(
        target === 'walk-in'
          ? `/insurance/pre-auths/new?kind=pharmacy&mode=walk-in&storeId=${encodeURIComponent(storeId)}`
          : `/insurance/pre-auths/new?kind=pharmacy&mode=prescription&patientId=${encodeURIComponent(target.patientId)}&prescriptionId=${encodeURIComponent(target.prescriptionId)}`,
        '/pharmacy',
      ),
    );
  const asksNphies = hasModule('nphies') && hasModule('pharmacy') && can('nphies.preauth.send');
  const [mapping, setMapping] = useState<{ id: string; drugName: string; strength: string | null } | null>(null);
  // A prescription is written in a consultation, on a ward or in emergency.
  const dispensesPrescriptions = ['opd', 'er', 'ipd'].some((m) => hasModule(m as never));

  const stores = useQuery({
    queryKey: ['pharmacy-stores'],
    queryFn: () => get<Store[]>('/pharmacy/stores'),
  });

  const counters = (stores.data ?? []).filter((store) => store.isSellingPoint);

  useEffect(() => {
    if (!storeId && counters.length) {
      setStoreId(counters[0].id);
    }
  }, [counters, storeId]);

  const rxQueue = useQuery({
    queryKey: ['rx-queue'],
    queryFn: () => get<QueuedRx[]>('/pharmacy/prescription-queue?limit=100'),
    refetchInterval: visibleInterval(60_000),
  });

  return (
    <>
      <PageHeader
        title={tr('pharmacy', 'pharmacy')}
        description={tr('pharmacy', 'counter_sales_the_prescription_queue_and_returns')}
        actions={
          <div className="flex items-center gap-2">
            {hasModule('wasfaty') && can('wasfaty.use') && (
              <Button variant="outline" onClick={() => setWasfatyOpen(true)}>
                {tr('pharmacy', 'wasfaty_prescription')}
              </Button>
            )}
            {asksNphies && storeId && (
              <Button variant="outline" onClick={() => askInsurer('walk-in')}>
                {tr('pharmacy', 'ask_the_insurer')}
              </Button>
            )}
            <Select value={storeId} onChange={(e) => setStoreId(e.target.value)} className="w-56">
              {counters.map((store) => (
                <option key={store.id} value={store.id}>
                  {storeLabel(store)}
                </option>
              ))}
            </Select>
          </div>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'counter', label: tr('pharmacy', 'counter') },
          // Prescriptions are written in a consultation. A medical store has no
          // consultations, so its queue could never fill — the tab was an empty
          // room with a sign on the door.
          ...(dispensesPrescriptions
            ? [
                {
                  id: 'queue',
                  label: tr('pharmacy', 'prescription_queue'),
                  badge: <Badge variant="warning">{rxQueue.data?.length ?? 0}</Badge>,
                },
              ]
            : []),
          ...(can('pharmacy.return') ? [{ id: 'returns', label: tr('pharmacy', 'returns') }] : []),
        ]}
      />

      {tab === 'counter' && storeId && (
        <Counter
          storeId={storeId}
          onSold={() => {
            void queryClient.invalidateQueries({ queryKey: ['rx-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['stock'] });
          }}
        />
      )}

      {wasfatyOpen && <WasfatyDialog open onClose={() => setWasfatyOpen(false)} />}
      {mapping && storeId && (
        <MapProductDialog
          item={mapping}
          storeId={storeId}
          onClose={() => setMapping(null)}
          onMapped={() => void queryClient.invalidateQueries({ queryKey: ['rx-queue'] })}
        />
      )}

      {tab === 'returns' && <SalesReturnPanel />}

      {tab === 'queue' && (
        <DataTable
          rows={rxQueue.data}
          loading={rxQueue.isLoading} refreshing={rxQueue.isFetching}
          error={rxQueue.error}
          rowKey={(row) => row.id}
          empty={tr('pharmacy', 'no_prescriptions_waiting')}
          columns={[
            {
              header: tr('pharmacy', 'patient'),
              sort: (row) => row.patient.fullName,
              cell: (row) => (
                <div>
                  <p className="font-medium">{row.patient.fullName}</p>
                  <p className="text-xs text-muted-foreground">{row.patient.mrn}</p>
                </div>
              ),
            },
            { header: tr('pharmacy', 'prescriber'), sort: (row) => row.doctor.name, cell: (row) => row.doctor.name },
            {
              header: tr('pharmacy', 'medicines'),
              cell: (row) => (
                <ul className="space-y-0.5 text-xs">
                  {row.items.map((item) => (
                    <li key={item.id}>
                      {item.drugName} {item.strength ?? ''} — {item.dose} {item.frequency}
                      {item.pendingQty > 0 ? (
                        <Badge variant="warning" className="ms-1">
                          {item.pendingQty} {tr('pharmacy', 'pending')}
                        </Badge>
                      ) : (
                        <Badge variant="success" className="ms-1">
                          {tr('pharmacy', 'done_2')}
                        </Badge>
                      )}
                      {item.isControlled && (
                        <Badge variant="destructive" className="ms-1">
                          {tr('pharmacy', 'controlled')}
                        </Badge>
                      )}
                      {!item.productId && item.pendingQty > 0 && (
                        <>
                          {item.mappedProductName && <span className="ms-1 text-muted-foreground">→ {item.mappedProductName}</span>}
                          {can('pharmacy.pos', 'rx.dispense') && (
                            <Button size="sm" variant="ghost" className="ms-1 h-6 px-2 text-xs" onClick={() => setMapping(item)}>
                              {item.mappedProductId ? tr('pharmacy', 'change_product') : tr('pharmacy', 'choose_product')}
                            </Button>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              ),
            },
            { header: tr('pharmacy', 'issued'), sort: (row) => row.issuedAt, cell: (row) => dateOnly(row.issuedAt) },
            {
              header: '',
              align: 'right',
              cell: (row) => (
                <div className="flex justify-end gap-1">
                  <PrintDocumentButton path={`/prescriptions/${row.id}/labels`} label={tr('pharmacy', 'print_dispensing_labels')} compact />
                  {asksNphies && (
                    <Button size="sm" variant="outline" onClick={() => askInsurer({ patientId: row.patient.id, prescriptionId: row.id })}>
                      {tr('pharmacy', 'ask_the_insurer')}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => {
                      setTab('counter');
                      window.dispatchEvent(new CustomEvent('his:dispense', { detail: row }));
                    }}
                  >
                    {tr('pharmacy', 'dispense')}
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}
    </>
  );
}

function Counter({ storeId, onSold }: { storeId: string; onSold: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const { hasModule } = useAuth();
  const treatsPatients = PATIENT_BEARING_MODULES.some(hasModule);
  const [patientId, setPatientId] = useState('');
  const [prescriptionId, setPrescriptionId] = useState<string | null>(null);
  const [walkInName, setWalkInName] = useState('');
  const [paying, setPaying] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 200);
    return () => clearTimeout(timer);
  }, [term]);

  // Loading a prescription from the queue fills the cart with what is still owed.
  useEffect(() => {
    const onDispense = (event: Event) => {
      const rx = (event as CustomEvent<QueuedRx>).detail;
      setPatientId(rx.patient.id);
      setPrescriptionId(rx.id);
      const owed: CartLine[] = rx.items
        .filter((item) => item.pendingQty > 0 && (item.productId || item.mappedProductId))
        .map((item) => ({
          productId: (item.productId ?? item.mappedProductId) as string,
          label: `${item.drugName} ${item.strength ?? ''}`.trim(),
          qty: item.pendingQty,
          priceMinor: 0,
          available: 0,
          discountPct: 0,
          taxPct: 0,
          batches: [],
          priced: false,
          prescriptionItemId: item.id,
        }));
      setCart(owed);
      if (owed.length === 0) return;
      const ids = [...new Set(owed.map((line) => line.productId))];
      get<SellableProduct[]>(`/pharmacy/search${qs({ storeId, ids: ids.join(',') })}`)
        .then((products) => {
          const byId = new Map(products.map((product) => [product.id, product]));
          setCart((current) =>
            current.map((line) => {
              const product = byId.get(line.productId);
              if (line.priced || !product) return line;
              return { ...line, priceMinor: product.priceMinor, available: product.available, taxPct: product.saleTaxPct, batches: product.batches, priced: true };
            }),
          );
          const short = owed.filter((line) => (byId.get(line.productId)?.available ?? 0) < line.qty);
          if (short.length > 0) toast.warning(tr('pharmacy', 'not_enough_stock_here_for_x', { items: short.map((line) => line.label).join(', ') }));
          else toast.success(tr('pharmacy', 'prescription_loaded'), tr('pharmacy', 'check_quantities_then_take_payment'));
        })
        .catch((caught) => toast.error(tr('pharmacy', 'prices_for_this_prescription_could_not_be_loaded'), describeError(caught)));
    };
    window.addEventListener('his:dispense', onDispense);
    return () => window.removeEventListener('his:dispense', onDispense);
  }, [toast, storeId]);

  const results = useQuery({
    queryKey: ['pharmacy-search', storeId, debounced],
    queryFn: () => get<SellableProduct[]>(`/pharmacy/search${qs({ storeId, q: debounced, limit: 15 })}`),
    enabled: debounced.length >= 2,
  });

  // A scanner types fast and ends with Enter; an exact single match is added
  // straight to the cart so the counter never stops to click.
  useEffect(() => {
    if (!results.data) return;
    if (debounced.length >= 8 && results.data.length === 1 && /^\d+$/.test(debounced)) {
      addToCart(results.data[0]);
      setTerm('');
    }
    // `addToCart` is redefined every render; depending on it would re-run this
    // effect continuously and could add the same scan twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.data, debounced]);

  const addToCart = (product: SellableProduct) => {
    if (product.available <= 0) {
      toast.error(tr('pharmacy', 'out_of_stock'), tr('pharmacy', 'x_has_none_available_in_this_store', { genericName: product.genericName }));
      return;
    }
    setCart((current) => {
      const existing = current.find((line) => line.productId === product.id);
      if (existing) {
        return current.map((line) =>
          line.productId === product.id ? { ...line, qty: line.qty + 1 } : line,
        );
      }
      return [
        ...current,
        {
          productId: product.id,
          label: [product.brandName ?? product.genericName, product.strength].filter(Boolean).join(' '),
          qty: 1,
          priceMinor: product.priceMinor,
          available: product.available,
          discountPct: 0,
          taxPct: product.saleTaxPct,
          batches: product.batches,
          priced: true,
        },
      ];
    });
    searchRef.current?.focus();
  };

  const digits = moneyDigits();
  const totals = useMemo(() => cartAmounts(cart, digits), [cart, digits]);
  const total = totals.net;
  const unpriced = cart.some((line) => !line.priced);

  const allowance = useQuery({
    queryKey: [PACKAGE_ALLOWANCE_KEY, storeId, patientId],
    queryFn: ({ signal }) => get<PosPackageAllowance>(`/pharmacy/package-allowance${qs({ storeId, patientId })}`, signal),
    enabled: Boolean(storeId && patientId),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const holdsAllowance = Boolean(patientId) && (allowance.data?.enrolments.length ?? 0) > 0;
  const requestLines = useMemo(() => quotableLines(cart), [cart]);
  const signature = quoteSignature(requestLines);
  const settledSignature = useDebounced(signature, QUOTE_DEBOUNCE_MS);
  const quoting = holdsAllowance && requestLines !== null && settledSignature === signature;
  const quote = useQuery({
    queryKey: [POS_QUOTE_KEY, storeId, patientId, prescriptionId, settledSignature],
    queryFn: ({ signal }) =>
      post<PosQuote>('/pharmacy/sales/quote', { storeId, patientId, prescriptionId: prescriptionId || undefined, lines: requestLines }, { signal }),
    enabled: quoting,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
  const packageView = quoting ? cartPackageView(quote.data, cart.length) : null;
  const quotePending = allowance.isLoading || (holdsAllowance && requestLines !== null && (!quoting || quote.isFetching));
  const quoteFailed = quoting && quote.isError;
  const refreshQuote = quote.refetch;
  const quoteCheckedAt = Math.max(quote.dataUpdatedAt, quote.errorUpdatedAt);
  const quoteBusy = quote.isFetching;

  useEffect(() => {
    if (paying && quoting && !quoteBusy && Date.now() - quoteCheckedAt > QUOTE_FRESH_MS) void refreshQuote();
  }, [paying, quoting, quoteBusy, quoteCheckedAt, refreshQuote]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'F2' && cart.length > 0 && !unpriced) {
        event.preventDefault();
        setPaying(true);
      }
      if (event.key === 'F3') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cart.length, unpriced]);

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="space-y-3 lg:col-span-2">
        <div className="relative">
          <Barcode className="pointer-events-none absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchRef}
            className="ps-8"
            placeholder={tr('pharmacy', 'scan_a_barcode_or_type_a_medicine_name_f3_to')}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            autoFocus
          />
        </div>

        {results.data && debounced.length >= 2 && (
          <div className="max-h-56 overflow-y-auto rounded-md border">
            {results.data.length === 0 && (
              <p className="p-3 text-sm text-muted-foreground">{tr('pharmacy', 'nothing_matches_that_in_this_store')}</p>
            )}
            {results.data.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => addToCart(product)}
                className="flex w-full items-center justify-between gap-3 border-b px-3 py-2 text-start last:border-0 hover:bg-accent focus-ring"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {product.brandName ?? product.genericName} {product.strength ?? ''}
                    {product.isControlled && <Badge variant="destructive" className="ms-1">{tr('pharmacy', 'controlled')}</Badge>}
                    {product.isRxRequired && !product.isControlled && (
                      <Badge variant="warning" className="ms-1">{tr('pharmacy', 'rx')}</Badge>
                    )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {product.genericName} · {product.form ?? ''} {tr('pharmacy', 'expires_2')} {dateOnly(product.nextExpiry)}
                  </p>
                </div>
                <div className="shrink-0 text-end">
                  <p className="tabular text-sm font-medium">{money(product.priceMinor)}</p>
                  <p className={`text-xs ${product.available > 0 ? 'text-muted-foreground' : 'text-destructive'}`}>
                    {product.available} {tr('pharmacy', 'in_stock')}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-sm">{tr('pharmacy', 'cart')}</CardTitle>
            {cart.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setCart([])}>
                {tr('pharmacy', 'clear')}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {cart.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {tr('pharmacy', 'scan_or_search_to_add_items')}
              </p>
            ) : (
              <div className="space-y-2">
                {cart.map((line, index) => (
                  <div key={line.productId} className="grid grid-cols-12 items-center gap-2 rounded-md border p-2">
                    <div className="col-span-5 min-w-0">
                      <p className="truncate text-sm">{line.label}</p>
                      <PackageBadges view={packageView?.lines[index] ?? null} />
                    </div>
                    <Input
                      className="col-span-2 tabular"
                      type="number"
                      min={1}
                      value={line.qty}
                      onChange={(e) =>
                        setCart((current) =>
                          current.map((l, i) => (i === index ? { ...l, qty: Number(e.target.value) } : l)),
                        )
                      }
                    />
                    <Input
                      className="col-span-2 tabular"
                      type="number"
                      min={0}
                      max={100}
                      value={line.discountPct}
                      onChange={(e) =>
                        setCart((current) =>
                          current.map((l, i) => (i === index ? { ...l, discountPct: Number(e.target.value) } : l)),
                        )
                      }
                      placeholder="%"
                    />
                    <span className="col-span-2 text-end tabular text-sm">
                      <LineAmount view={packageView?.lines[index] ?? null} fallback={line.priced ? money(cartLineAmounts(line, digits).net) : '…'} />
                    </span>
                    <Button
                      className="col-span-1"
                      variant="ghost"
                      size="icon"
                      aria-label={tr('pharmacy', 'remove')}
                      onClick={() => setCart((current) => current.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('pharmacy', 'customer')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* A standalone medical store keeps no patient records, so the
                picker would only ever come up empty. Its own walk-in name is
                the customer. */}
            {treatsPatients && (
              <Field label={tr('pharmacy', 'patient')} hint={tr('pharmacy', 'leave_blank_for_a_walk_in')}>
                <PatientPicker value={patientId} onChange={setPatientId} />
              </Field>
            )}
            {!patientId && (
              <Field label={tr('pharmacy', 'walk_in_name')}>
                <Input value={walkInName} onChange={(e) => setWalkInName(e.target.value)} />
              </Field>
            )}
            {prescriptionId && (
              <Alert variant="success">
                {tr('pharmacy', 'dispensing_against_a_prescription')}{' '}
                <button className="underline" onClick={() => setPrescriptionId(null)}>
                  {tr('pharmacy', 'detach')}
                </button>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">{tr('pharmacy', 'total')}</span>
              <span className="tabular text-3xl font-semibold">{money(total)}</span>
            </div>
            {totals.tax > 0 && (
              <p className="text-end text-xs text-muted-foreground">{tr('pharmacy', 'includes_tax_x', { tax: money(totals.tax) })}</p>
            )}
            {packageView && packageView.coveredMinor > 0 && (
              <div className="mt-3 space-y-1 border-t pt-3">
                <p className="text-sm text-success">{tr('pharmacy', 'package_covers_x', { amount: money(packageView.coveredMinor) })}</p>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">{tr('pharmacy', 'to_collect')}</span>
                  <span className="tabular text-2xl font-semibold">{money(packageView.dueMinor)}</span>
                </div>
              </div>
            )}
            {packageView?.holder && (
              <p className="mt-1 text-xs text-muted-foreground">
                {tr('pharmacy', 'x_allowance_left_after_this_sale_y', { name: packageView.holder.name, amount: money(packageView.holder.allowanceLeftAfterMinor) })}
              </p>
            )}
            {quotePending && !packageView && <p className="mt-2 text-xs text-muted-foreground">{tr('pharmacy', 'checking_the_patients_package')}</p>}
            {quoteFailed && <p className="mt-2 text-xs text-muted-foreground">{tr('pharmacy', 'package_could_not_be_checked')}</p>}
            {unpriced && <p className="mt-2 text-xs text-muted-foreground">{tr('pharmacy', 'waiting_for_prices_remove_lines_without_a_price')}</p>}
            <Button className="mt-3 w-full" size="lg" disabled={cart.length === 0 || unpriced} onClick={() => setPaying(true)}>
              {tr('pharmacy', 'take_payment_f2')}
            </Button>
          </CardContent>
        </Card>
      </div>

      {paying && (
        <PaymentDialog
          open={paying}
          onClose={() => setPaying(false)}
          storeId={storeId}
          patientId={patientId}
          walkInName={walkInName}
          prescriptionId={prescriptionId}
          cart={cart}
          total={total}
          quotedDue={packageView ? packageView.dueMinor : null}
          quotePending={quotePending}
          quoteFailed={quoteFailed}
          onSold={() => {
            setPaying(false);
            setCart([]);
            setPrescriptionId(null);
            setPatientId('');
            setWalkInName('');
            if (holdsAllowance) {
              void queryClient.invalidateQueries({ queryKey: [PACKAGE_ALLOWANCE_KEY] });
              queryClient.removeQueries({ queryKey: [POS_QUOTE_KEY] });
            }
            onSold();
          }}
        />
      )}
    </div>
  );
}

function PaymentDialog({
  open,
  onClose,
  storeId,
  patientId,
  walkInName,
  prescriptionId,
  cart,
  total,
  quotedDue,
  quotePending,
  quoteFailed,
  onSold,
}: {
  open: boolean;
  onClose: () => void;
  storeId: string;
  patientId: string;
  walkInName: string;
  prescriptionId: string | null;
  cart: CartLine[];
  total: number;
  quotedDue: number | null;
  quotePending: boolean;
  quoteFailed: boolean;
  onSold: () => void;
}) {
  const toast = useToast();
  const [mode, setMode] = useState('cash');
  const [tendered, setTendered] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());
  const [discountReason, setDiscountReason] = useState('');
  const [approval, setApproval] = useState<string | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [serverDue, setServerDue] = useState<number | null>(null);
  const discounted = cart.some((line) => line.discountPct > 0);
  const quoted = quotedDue !== null;
  const due = amountToCollect({ totalMinor: total, quoteDueMinor: quotedDue, serverDueMinor: serverDue });

  useEffect(() => {
    if (open) {
      setIdempotencyKey(newIdempotencyKey());
      setError(null);
      setServerDue(null);
    }
  }, [open, total, quotedDue]);

  useEffect(() => {
    setTendered(formatMoneyInput(due));
  }, [due]);

  const tenderedMinor = minorFromInput(tendered);
  const change = tenderedMinor - due;

  const sell = useMutation({
    mutationFn: (supervisor?: SupervisorCredentials) =>
      post<{ invoiceId: string; number: string; totals: { balanceMinor: number } }>(
        '/pharmacy/sales',
        {
          storeId,
          patientId: patientId || undefined,
          prescriptionId: prescriptionId || undefined,
          walkInName: !patientId ? walkInName || 'Walk-in' : undefined,
          lines: saleLines(cart),
          payments: due > 0 ? [{ mode, amountMinor: due }] : [],
          ...(quoted ? { expectedDueMinor: due } : {}),
          discountReason: discounted ? discountReason.trim() || undefined : undefined,
        },
        { idempotencyKey, headers: supervisorHeaders(supervisor ?? null) },
      ),
    onSuccess: async (sale) => {
      setApproval(null);
      if (sale.totals.balanceMinor > 0) {
        toast.warning(
          tr('pharmacy', 'sale_x_is_not_fully_paid', { number: sale.number }),
          tr('pharmacy', 'x_is_still_owed_on_this_bill', { balance: money(sale.totals.balanceMinor) }),
        );
      } else {
        toast.success(tr('pharmacy', 'sale_x_complete', { number: sale.number }), change > 0 ? tr('pharmacy', 'change_x', { change: money(change) }) : undefined);
      }
      onSold();
      const receipt = await get<{ html: string }>(`/invoices/${sale.invoiceId}/print?size=thermal`);
      printDocument(receipt.html);
    },
    onError: (caught, supervisor) => {
      const left = caught instanceof ApiError ? refusedDue(caught.details, due) : null;
      if (left !== null) {
        setServerDue(left);
        setIdempotencyKey(newIdempotencyKey());
        setApproval(null);
        setError(null);
        return;
      }
      if (needsDiscountApproval(caught) && !supervisor) {
        setApproval(describeError(caught));
        return;
      }
      if (supervisor) {
        setApprovalError(describeError(caught));
        return;
      }
      setError(describeError(caught));
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={tr('pharmacy', 'take_payment')}
      description={tr('pharmacy', 'x_item_s', { length: cart.length })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('pharmacy', 'cancel')}
          </Button>
          <Button
            onClick={() => sell.mutate(undefined)}
            loading={sell.isPending}
            disabled={quotePending || (mode === 'cash' && change < 0) || (discounted && discountReason.trim().length === 0)}
          >
            {tr('pharmacy', 'complete_sale')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {discounted && (
          <Field label={tr('pharmacy', 'reason_for_discount')} required hint={tr('pharmacy', 'required_whenever_a_discount_is_given')}>
            <Input value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} />
          </Field>
        )}
        {approval && (
          <SupervisorDialog
            open
            title={tr('pharmacy', 'discount_above_your_limit')}
            message={approval}
            busy={sell.isPending}
            error={approvalError}
            onClose={() => {
              setApproval(null);
              setApprovalError(null);
            }}
            onApprove={(credentials) => {
              setApprovalError(null);
              sell.mutate(credentials);
            }}
          />
        )}
        <div className="flex items-baseline justify-between rounded-md bg-muted/50 px-3 py-2">
          <span className="text-sm text-muted-foreground">{tr('pharmacy', 'amount_due')}</span>
          <span className="tabular text-2xl font-semibold">{money(due)}</span>
        </div>
        {quotePending && <p className="text-xs text-muted-foreground">{tr('pharmacy', 'checking_the_patients_package')}</p>}
        {quoted && serverDue !== null ? (
          <Alert variant="warning">{tr('pharmacy', 'amount_due_changed_collect_x', { due: money(due) })}</Alert>
        ) : (
          due < total && <Alert variant="success">{tr('pharmacy', 'package_covers_part_of_this_sale', { total: money(total), due: money(due) })}</Alert>
        )}
        {quoteFailed && <p className="text-xs text-muted-foreground">{tr('pharmacy', 'package_could_not_be_checked')}</p>}

        <Field label={tr('pharmacy', 'payment_mode')}>
          <ChoiceButtons
            layout="chips"
            value={mode}
            onChange={setMode}
            options={['cash', 'card', 'mobile_wallet', 'bank_transfer'].map((m) => ({ value: m, label: humanize(m, 'payment_mode') }))}
          />
        </Field>

        {mode === 'cash' && (
          <Field label={tr('pharmacy', 'cash_tendered')}>
            <Input
              type="text"
              inputMode="decimal"
              dir="ltr"
              className="text-lg tabular"
              value={tendered}
              onChange={(e) => setTendered(e.target.value)}
              autoFocus
            />
          </Field>
        )}

        {mode === 'cash' && (
          <Alert variant={change < 0 ? 'destructive' : change > 0 ? 'warning' : 'success'}>
            {change < 0
              ? tr('pharmacy', 'short_by_x', { value: money(Math.abs(change)) })
              : change > 0
                ? tr('pharmacy', 'change_to_return_x', { change: money(change) })
                : tr('pharmacy', 'exact_amount')}
          </Alert>
        )}

        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
